import {
  FilterContainer,
  GroupedUsersMultiSelect,
  MultiSelect,
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import useUserSkills from 'screens/Rostering/hooks/useUserSkills'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { Switch } from 'antd'
import { VISIT } from 'screens/Rostering/constants'
import useUsersList from 'screens/Rostering/hooks/useUsersList'

export type AllocationFiltersValue = {
  teamMembers?: string[]
  role?: string[]
  availability?: string[]
  skills?: string[]
  careTeamOnly?: boolean
  selected?: boolean
}

type AllocationFiltersProps = {
  type?: 'shift' | 'visit'
  value?: AllocationFiltersValue
  onChange: (value: AllocationFiltersValue) => void
}

const AllocationFilters = ({
  type,
  value,
  onChange,
}: AllocationFiltersProps) => {
  const { t } = useTranslation()

  const roleValues = ['Carer', 'Senior Carer', 'Admin']
  const availabilityValues = ['Available', 'Allocated', 'Absent']

  const { allSkills } = useUserSkills()

  const { users } = useUsersList({ active: true })

  const skillsList = allSkills?.map((skill) => ({
    label: skill?.name,
    value: skill?.id,
  }))

  const transformOptions = (options: string[]) => {
    return options.map((option) => ({ value: option, label: option }))
  }

  const handleInputChange = (newValue: Partial<AllocationFiltersValue>) => {
    const updatedValue: AllocationFiltersValue = {
      ...value,
      ...newValue,
    }
    onChange(updatedValue)
  }

  return (
    <FilterContainer title="Filters">
      <Filters>
        <LeftContainer>
          <GroupedUsersMultiSelect
            users={users}
            title={t('TEAM_MEMBERS')}
            values={value?.teamMembers}
            handleInputChange={(selectedOptions) =>
              handleInputChange({
                teamMembers: selectedOptions as string[],
              })
            }
          />
          <StyledMultiSelect
            title={t('ROLE')}
            options={transformOptions(roleValues)}
            hasClear
            hasSearch
            hasSelectAll
            onChange={(selectedRoles) =>
              handleInputChange({
                role: selectedRoles.map((role) => {
                  switch (role) {
                    case 'Carer':
                      return 'carer'
                    case 'Senior Carer':
                      return 'senior-carer'
                    case 'Admin':
                      return 'carehome-admin'
                    default:
                      return role
                  }
                }) as string[],
              })
            }
          />
          <StyledMultiSelect
            title={t('AVAILABILITY')}
            options={transformOptions(availabilityValues)}
            hasClear
            hasSearch
            hasSelectAll
            onChange={(selectedAvailabilities) =>
              handleInputChange({
                availability: selectedAvailabilities as string[],
              })
            }
          />
          <StyledWideMultiSelect
            values={value?.skills}
            title={t('SKILLS')}
            options={skillsList}
            hasClear
            hasSearch
            hasSelectAll
            onChange={(selectedSkills) =>
              handleInputChange({
                skills: selectedSkills as string[],
              })
            }
          />
        </LeftContainer>
        {type === VISIT && (
          <RightContainer>
            <SpotlightText weight="medium">{t('CARE_TEAM_ONLY')}</SpotlightText>
            <Switch
              value={value?.careTeamOnly}
              onChange={(careTeam) =>
                handleInputChange({
                  careTeamOnly: careTeam,
                })
              }
            />
          </RightContainer>
        )}
      </Filters>
    </FilterContainer>
  )
}

const LeftContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const StyledMultiSelect = styled(MultiSelect)`
  min-width: 160px;
`
const StyledWideMultiSelect = styled(MultiSelect)`
  min-width: 200px;
` as unknown as typeof MultiSelect

const Filters = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`
const RightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const SpotlightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

export default AllocationFilters
